import React, { useContext, useState, useEffect, useRef } from 'react';

import Cookies from 'js-cookie';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import experiments from '@/utils/labs/experiments';
import { updateEmailSubscription, updatePushSubscription } from '@/client/actions/savedSearches';
import SaveSearchButton from '@/client/components/SaveSearch/SaveSearchButton';
import SaveSearchModal from '@/client/components/SaveSearch/SaveSearchModal';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import showToastNotification from '@/client/utils/showToastNotification';
import { getIsDesktop, getIsTabletAdaptingMobile, getIsSmallMobile } from '@/client/utils/browser/isMobile';

import { createSavedSearch, redirectToLoginPage } from './utils';
import Classes from './SaveSearchContainer.module.scss';

const SAVE_SEARCH_COOKIE_KEY = 'saveSearchToggleLoaded';

interface SaveSearchContainerProps {
  searchRequestObject: TSearchRequestObject;
  className?: string;
  isSearchSaved: boolean;
  savedSearchId: number | null;
  setIsSearchSaved: (isSearchSaved: boolean) => void;
  setSavedSearchId: (id: number) => void;
}

const SaveSearchContainer: React.FC<SaveSearchContainerProps> = ({
  searchRequestObject,
  className,
  setIsSearchSaved,
  setSavedSearchId,
  savedSearchId,
  isSearchSaved = false,
}) => {
  const {
    xsrfToken,
    userDetails: { isLoggedIn },
    labsConfig,
    isMobileUserAgent,
  } = useContext(EnvironmentContext);
  const { isFloatingSaveSearchButtonEnabled } = experiments({ labsConfig });
  const { t } = useI18nContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [showSmallButton, setShowSmallButton] = useState(false);
  const [isTabletAdaptingMobile, setIsTabletAdaptingMobile] = useState(false);
  const [isSearchSavedSuccessfully, setIsSearchSavedSuccessfully] = useState(false);
  const closeToast = useRef<(() => void) | null>(null);
  const shouldShowFloatingButton = isFloatingSaveSearchButtonEnabled && isMobileUserAgent;

  useEffect(() => {
    setIsClient(true);
    setShowSmallButton(!getIsDesktop() && !getIsSmallMobile());
    setIsTabletAdaptingMobile(getIsTabletAdaptingMobile());
  }, []);

  useEffect(() => {
    const currentCloseToast = closeToast.current;
    // Dismiss any existing toast when component unmounts
    return () => {
      if (currentCloseToast) {
        currentCloseToast();
      }
    };
  }, []);

  const handleGoToSavedSearchesPage = () => {
    window?.location?.assign('/my-account/saved-searches/index.html');
  };

  const handleOpenModal = async () => {
    if (isSearchSaved) {
      handleGoToSavedSearchesPage();
      return;
    }

    if (!isLoggedIn) {
      Cookies.set(SAVE_SEARCH_COOKIE_KEY, 'true');
      redirectToLoginPage();
      return;
    }

    const defaultPreferences = { emailEnabled: true, pushEnabled: true };

    await createSavedSearch(xsrfToken, searchRequestObject, defaultPreferences, {
      setIsSearchSaved,
      setSavedSearchId,
      setIsSearchSavedSuccessfully: (success) => {
        setIsSearchSavedSuccessfully(success);
        if (success) {
          setIsModalOpen(true);
        }
      },
      t,
      closeToast,
      shouldShowFloatingButton,
    });
  };

  const handleCloseModal = async () => {
    setIsModalOpen(false);

    if (isSearchSavedSuccessfully) {
      await showToastNotification('success', t('saveSearch.messages.createSuccess'), closeToast);
    }
  };

  const handleEmailToggle = async (emailEnabled: boolean): Promise<boolean> => {
    if (!savedSearchId) {
      return false;
    }

    try {
      await updateEmailSubscription(xsrfToken, emailEnabled, savedSearchId);
      return true;
    } catch {
      await showToastNotification('error', t('messages.generic_error'), closeToast);
      return false;
    }
  };

  const handlePushToggle = async (pushEnabled: boolean): Promise<boolean> => {
    if (!savedSearchId) {
      return false;
    }

    try {
      await updatePushSubscription(xsrfToken, pushEnabled, savedSearchId);
      return true;
    } catch {
      await showToastNotification('error', t('messages.generic_error'), closeToast);
      return false;
    }
  };

  useEffect(() => {
    const saveSearchFlag = Cookies.get(SAVE_SEARCH_COOKIE_KEY);
    if (saveSearchFlag) {
      handleOpenModal();
      Cookies.remove(SAVE_SEARCH_COOKIE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      suppressHydrationWarning
      className={`${className} ${Classes.saveSearchControl}`}
      data-testid="saveSearchControl"
    >
      <SaveSearchButton
        onClick={handleOpenModal}
        isSearchSaved={isSearchSaved}
        smallButton={showSmallButton}
        isTabletAdaptingMobile={isTabletAdaptingMobile}
        shouldShowFloatingButton={shouldShowFloatingButton}
      />
      <Trans tagName="p" i18nKey="saveSearch.messages.invite" />
      {isClient && isModalOpen && (
        <SaveSearchModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          handleGoToSavedSearchesPage={handleGoToSavedSearchesPage}
          onEmailToggle={handleEmailToggle}
          onPushToggle={handlePushToggle}
          initialEmailEnabled={true}
          initialPushEnabled={true}
        />
      )}
    </div>
  );
};

export default SaveSearchContainer;
